import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Settings,
  Lock,
  Unlock,
  Eye,
  Save,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  User,
  Calendar
} from 'lucide-react';
import { bondsAPI, formatDate } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TokenPermissions = () => {
  const navigate = useNavigate();
  const { getDisplayName } = useAuth();
  const [selectedToken, setSelectedToken] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [permissionChanges, setPermissionChanges] = useState({});


  // Helper function to check if bond is matured
  const getMaturityStatus = (maturityDate) => {
    if (!maturityDate) return false;
    const today = new Date();
    const maturity = new Date(maturityDate);
    return maturity < today;
  };

  // Fetch bonds from API
  const { data: bondsData, isPending: bondsLoading, error: bondsError } = useQuery({
    queryKey: ['bonds', 'token-permissions'],
    queryFn: () => bondsAPI.getBonds({
      page: 1,
      limit: 50
    }),
    select: data => {
      const bonds = data.data.bonds || [];
      // Transform API bond data to match the expected format for token permissions
      return bonds.map(bond => ({
        id: bond._id,
        bondName: bond.bondName,
        bondId: bond.bondNumber || bond._id,
        tokenSymbol: bond.tokenSymbol || `${bond.bondName?.replace(/\s+/g, '').slice(0, 6).toUpperCase()}`,
        totalSupply: bond.issueAmount || 0,
        circulatingSupply: bond.issueAmount || 0, // In real scenario, this would come from blockchain
        maturityDate: bond.maturityDate,
        isMatured: getMaturityStatus(bond.maturityDate),
        permissions: {
          transferable: bond.transferable !== false, // Default to true
          tradeable: bond.tradeable !== false,
          redeemable: getMaturityStatus(bond.maturityDate), // Enable redemption for matured bonds
          burnable: getMaturityStatus(bond.maturityDate), // Enable burning for matured bonds
          mintable: bond.mintable === true // Default to false
        },
        lastModified: bond.updatedAt || bond.createdAt,
        modifiedBy: 'Issuer Admin',
        auditTrail: [
          {
            action: 'Permission Created',
            timestamp: bond.createdAt,
            user: 'System',
            details: 'Initial token permissions set during issuance'
          }
        ],
        // Additional bond data
        isin: bond.isin,
        issuer: bond.issuer,
        productType: bond.productType,
        status: bond.status,
        couponRate: bond.couponRate,
        profitRate: bond.profitRate
      }));
    }
  });

  const bondTokens = bondsData || [];

  // Handle loading state
  if (bondsLoading) {
    return <LoadingSpinner size="lg" text="Loading bond data..." />;
  }

  // Handle error state
  if (bondsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Bonds</h3>
          <p className="mt-1 text-sm text-gray-500">
            {bondsError.message || 'Failed to load bond data'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getPermissionBadge = (permission, value) => {
    const config = value
      ? { color: 'green', icon: CheckCircle, text: 'Enabled' }
      : { color: 'red', icon: XCircle, text: 'Disabled' };

    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const getMaturityBadge = (isMatured) => {
    return isMatured ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <Lock className="w-3 h-3 mr-1" />
        Matured
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <Clock className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const handleConfigurePermissions = (token) => {
    setSelectedToken(token);
    setPermissionChanges(token.permissions);
    setShowPermissionModal(true);
  };

  const handleViewAudit = (token) => {
    setSelectedToken(token);
    setShowAuditModal(true);
  };

  const handlePermissionChange = (permission, value) => {
    setPermissionChanges({
      ...permissionChanges,
      [permission]: value
    });
  };

  const confirmPermissionUpdate = () => {
    if (!confirmationCode.trim()) {
      alert('Please enter confirmation code to proceed.');
      return;
    }

    const auditRecord = {
      action: 'Permission Modified',
      timestamp: new Date().toISOString(),
      user: getDisplayName(),
      details: `Updated permissions: ${Object.entries(permissionChanges)
        .filter(([key, value]) => selectedToken.permissions[key] !== value)
        .map(([key, value]) => `${key}: ${value ? 'enabled' : 'disabled'}`)
        .join(', ')}`,
      confirmationCode: confirmationCode
    };

    console.log('Permission update audit record:', auditRecord);
    alert(`Token permissions updated successfully!\n\nAudit Record Generated:\nAction: ${auditRecord.action}\nUser: ${auditRecord.user}\nTimestamp: ${auditRecord.timestamp}\nConfirmation Code: ${auditRecord.confirmationCode}`);

    setShowPermissionModal(false);
    setSelectedToken(null);
    setPermissionChanges({});
    setConfirmationCode('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Token Permissions</h1>
              <p className="text-sm text-gray-600">Configure and manage token permissions with audit controls</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Bond Tokens
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {bondTokens.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Tokens
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {bondTokens.filter(t => !t.isMatured).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Lock className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Matured Tokens
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {bondTokens.filter(t => t.isMatured).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Recent Changes
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {bondTokens.filter(t =>
                        new Date(t.lastModified) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                      ).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Token Permissions Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Bond Token Permissions</h3>
            <p className="text-sm text-gray-600">Manage permissions for all issued bond tokens</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supply Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Maturity Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transferable
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tradeable
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Redeemable
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Modified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bondTokens.map((token) => (
                  <tr key={token.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {token.bondName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {token.tokenSymbol} • {token.bondId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {token.circulatingSupply.toLocaleString()} / {token.totalSupply.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {((token.circulatingSupply / token.totalSupply) * 100).toFixed(1)}% in circulation
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getMaturityBadge(token.isMatured)}
                        <div className="text-xs text-gray-500">
                          {formatDate(token.maturityDate)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPermissionBadge('transferable', token.permissions.transferable)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPermissionBadge('tradeable', token.permissions.tradeable)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPermissionBadge('redeemable', token.permissions.redeemable)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(token.lastModified)}
                      </div>
                      <div className="text-sm text-gray-500">
                        by {token.modifiedBy}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleConfigurePermissions(token)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Configure
                        </button>
                        <button
                          onClick={() => handleViewAudit(token)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Audit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Permission Configuration Modal */}
      {showPermissionModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center mb-4">
              <Settings className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Configure Token Permissions
              </h3>
            </div>

            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-blue-900">{selectedToken.bondName}</h4>
                <p className="text-sm text-blue-700 mt-1">
                  {selectedToken.tokenSymbol} • Maturity: {formatDate(selectedToken.maturityDate)}
                </p>
                {selectedToken.isMatured && (
                  <p className="text-sm text-blue-700 font-medium mt-2">
                    ⚠️ Post-maturity token - Enhanced security controls apply
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-gray-900">Transferable</label>
                        <p className="text-xs text-gray-500">Allow token transfers between wallets</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissionChanges.transferable}
                        onChange={(e) => handlePermissionChange('transferable', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-gray-900">Tradeable</label>
                        <p className="text-xs text-gray-500">Allow token trading on exchanges</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissionChanges.tradeable}
                        onChange={(e) => handlePermissionChange('tradeable', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-gray-900">Redeemable</label>
                        <p className="text-xs text-gray-500">Allow token redemption for cash</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissionChanges.redeemable}
                        onChange={(e) => handlePermissionChange('redeemable', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-gray-900">Burnable</label>
                        <p className="text-xs text-gray-500">Allow permanent token destruction</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissionChanges.burnable}
                        onChange={(e) => handlePermissionChange('burnable', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-gray-900">Mintable</label>
                        <p className="text-xs text-gray-500">Allow creation of additional tokens</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissionChanges.mintable}
                        onChange={(e) => handlePermissionChange('mintable', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Security Confirmation Required</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Permission changes require confirmation code and will generate an audit record.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmation Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    placeholder="Enter authorization code"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPermissionModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmPermissionUpdate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Update Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Modal */}
      {showAuditModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center mb-4">
              <Eye className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Audit Trail - {selectedToken.bondName}
              </h3>
            </div>

            <div className="mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-green-900">{selectedToken.bondName}</h4>
                <p className="text-sm text-green-700 mt-1">
                  {selectedToken.tokenSymbol} • {selectedToken.bondId}
                </p>
              </div>

              <div className="space-y-4">
                {selectedToken.auditTrail.map((record, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {record.action}
                        </span>
                        <span className="text-sm text-gray-500">
                          by {typeof record.user === 'string' ? record.user : 'Unknown User'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDate(record.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{record.details}</p>
                    {record.confirmationCode && (
                      <p className="text-xs text-gray-500 mt-2">
                        Confirmation Code: {record.confirmationCode}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowAuditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default TokenPermissions; 